import { useState, useEffect } from 'react';
import { useMediaQuery } from "react-responsive";
import { Alert, Box, Stepper, Step, StepLabel, Button, styled, CircularProgress } from '@mui/material';
import { useHistory, useRouteMatch, Switch, Route, Redirect } from 'react-router-dom';
import { Container } from '@mui/material';
import { toast, Bounce } from 'react-toastify';
import { api } from '../services/api';
import Loading from './Loading';
import CrearEvento from '../views/eventos/evento/CrearEvento';
import EditarInformacion from '../views/eventos/evento/EditarInformacion';
import { SegundoPaso } from '../views/eventos/evento/SegundoPaso';
import { AgregarColaboradores } from '../views/eventos/evento/AgregarColaboradores';
import { PublicarEvento } from '../views/eventos/evento/PublicarEvento';
import { Check } from '@mui/icons-material';
import useEventoStore from '../stores/useEventoStore';

const steps = [
	'Información',
	'Tarifas',
	'Colaboradores',
	'Publicar'
];

// segmento de la URL de cada paso con el evento ya creado:
// /crearEvento/:eventId/informacion, /tarifas, ... Antes de existir, el primer
// paso es /crearEvento/creacion.
const PASOS_URL = ['informacion', 'tarifas', 'colaboradores', 'publicar'];

const ESTADO_BORRADOR = 1;

const urlPaso = (eventId, paso) => `/crearEvento/${eventId}/${PASOS_URL[paso]}`;

// estilos para los iconos del stepper
const CustomStepIcon = styled('div')(({ theme, ownerState }) => ({
	display: 'flex',
	height: 35,
	width: 35,
	borderRadius: '50%',
	justifyContent: 'center',
	alignItems: 'center',
	fontWeight: 600,
	fontSize: 16,
	background: ownerState.completed ? '#5656ff' : 'white',
	border: `2px solid ${ownerState.active || ownerState.completed ? '#5656ff' : '#e0e0e0'}`,
	color: ownerState.completed ? 'white' : '#0b0062',
	zIndex: 1,
}));

function StepIconComponent(props) {
	const { active, completed, className, icon } = props;

	return (
		<CustomStepIcon ownerState={{ active, completed }} className={className}>
			{completed ? <Check fontSize="small" style={{ color: 'white' }} /> : icon}
		</CustomStepIcon>
	);
}

const StyledStepLabel = styled(StepLabel)(() => ({
	'& .MuiStepLabel-label': {
		color: '#0b0062',
		fontFamily: 'Bricolage Grotesque',
		fontSize: "16px",
		marginTop: 8,
		'&.Mui-active': {
			fontWeight: '600',
		},
	},
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
	'& .MuiStepConnector-line': {
		borderTopWidth: 2,
		borderColor: '#e0e0e0',
	},
	'& .MuiStepConnector-active .MuiStepConnector-line': {
		borderColor: '#5656ff',
	},
	'& .MuiStepConnector-completed .MuiStepConnector-line': {
		borderColor: '#5656ff',
	},
}));

export const StepperCrearEvento = () => {
	const history = useHistory();
	const isMobile = useMediaQuery({ query: `(max-width: 770px)` });

	// el id del evento y el paso salen de la URL: así se puede recargar, usar
	// atrás/adelante del navegador o retomar un borrador desde otro lado
	const match = useRouteMatch('/crearEvento/:eventId(\\d+)/:paso');
	const eventId = match ? match.params.eventId : null;
	const pasoUrl = match ? PASOS_URL.indexOf(match.params.paso) : 0;
	const activeStep = pasoUrl > 0 ? pasoUrl : 0;

	const [isPublic, setIsPublic] = useState(false);
	const [idCargado, setIdCargado] = useState(null);
	// se incrementa para volver a leer el evento (p. ej. si en "Información"
	// cambió de público a privado, cambian los pasos)
	const [recargas, setRecargas] = useState(0);
	const [errorEvento, setErrorEvento] = useState(null);
	const [isFinalizingEvent, setIsFinalizingEvent] = useState(false);

	// se calcula en vez de guardarse para que el primer render con un id nuevo
	// ya cuente como cargando (si no, /publicar redirige antes de saber si es público)
	const cargandoEvento = Boolean(eventId) && idCargado !== eventId && !errorEvento;
	const stepsToShow = isPublic ? steps : steps.slice(0, 3);

	// el evento recién creado pasa directo a tarifas. Se reemplaza la entrada del
	// historial para que "atrás" no vuelva al formulario vacío y cree otro evento
	const handleEventoCreado = (id) => {
		history.replace(urlPaso(id, 1));
	};

	const finalizarEvento = async () => {
		setIsFinalizingEvent(true);

		try {
			// la publicación y sus preguntas se guardan en su propio paso: acá
			// sólo se confirma el evento
			const response = await api.eventos.confirmEvento(eventId);
			if (response.status === 'success') {
				toast.success('Tu evento se ha creado exitosamente', {
					position: 'top-center',
					autoClose: 3000,
					theme: 'dark',
					transition: Bounce,
				});

				setTimeout(() => {
					history.push(`/eventos/${eventId}`);
				}, 2000);
			} else {
				throw new Error(response.message || 'Error al confirmar el evento');
			}
		} catch (error) {
			toast.error(`Error: ${error.message}`, {
				position: 'top-center',
				autoClose: 6000,
				theme: 'dark',
				transition: Bounce,
			});
		} finally {
			setIsFinalizingEvent(false);
		}
	};

	const handleInformacionGuardada = () => {
		setIdCargado(null);
		setRecargas(n => n + 1);
		history.push(urlPaso(eventId, 1));
	};

	const handleBack = () => {
		history.push(urlPaso(eventId, activeStep - 1));
	};

	const handleNext = () => {
		// en privados el último paso es colaboradores: publicar no se muestra
		if (activeStep === stepsToShow.length - 1) {
			finalizarEvento();
		} else {
			history.push(urlPaso(eventId, activeStep + 1));
		}
	};

	const canProceed = () => {
		if (!eventId || cargandoEvento || errorEvento) return false;
		if (isFinalizingEvent) return false;
		return true;
	};

	//cambiar el boton dependiendo el paso
	const getButtonText = () => {
		if (isFinalizingEvent) return 'Finalizando...';
		if (activeStep === stepsToShow.length - 1) return 'Finalizar evento';
		return 'Siguiente';
	};

	// con el id de la URL se trae el evento: define si es público (y por lo tanto
	// si aparece el paso de publicación) y si sigue siendo un borrador
	useEffect(() => {
		if (!eventId) return;

		let cancelado = false;
		setErrorEvento(null);

		useEventoStore.getState().fetchEvento(eventId)
			.then(data => {
				if (cancelado) return;
				if (!data) {
					setErrorEvento('No encontramos el evento o no tenés acceso a él.');
					return;
				}

				const eventoData = data.evento;
				const estado = eventoData.estado?.id ?? eventoData.id_estados_eventos;
				if (estado !== ESTADO_BORRADOR) {
					// ya está confirmado: no tiene sentido volver a pasar por el alta
					history.replace(`/eventos/${eventId}`);
					return;
				}

				setIsPublic(eventoData.privado === 0 || eventoData.privado === false);
				setIdCargado(eventId);
			})
			.catch(error => {
				if (cancelado) return;
				console.error("Error al obtener datos del evento:", error);
				setErrorEvento('No se pudo cargar el evento.');
			});

		return () => { cancelado = true; };
	}, [eventId, history, recargas]);

	const renderStepper = () => (
		<Box sx={{ width: isMobile ? '100%' : '800px', margin: isMobile ? '0 auto' : '20px auto', my: 4 }}>
			<StyledStepper activeStep={activeStep} alternativeLabel>
				{stepsToShow.map((label, index) => (
					<Step key={label} completed={index < activeStep}>
						<StyledStepLabel StepIconComponent={StepIconComponent}>
							{label}
						</StyledStepLabel>
					</Step>
				))}
			</StyledStepper>
		</Box>
	);

	const renderPasos = () => {
		if (errorEvento) {
			return (
				<Container className="my-3" maxWidth="lg">
					<Alert
						severity="error"
						action={<Button color="inherit" size="small" onClick={() => history.push('/eventos')}>Ir a mis eventos</Button>}
					>
						{errorEvento}
					</Alert>
				</Container>
			);
		}

		if (cargandoEvento) {
			return <Loading />;
		}

		return (
			<Switch>
				<Route exact path="/crearEvento/:eventId(\d+)/informacion">
					<EditarInformacion eventId={eventId} onGuardado={handleInformacionGuardada} />
				</Route>
				<Route exact path="/crearEvento/creacion">
					<CrearEvento onCreado={handleEventoCreado} />
				</Route>
				<Route exact path="/crearEvento/:eventId(\d+)/tarifas">
					<SegundoPaso eventId={eventId} isPublic={isPublic} />
				</Route>
				<Route exact path="/crearEvento/:eventId(\d+)/colaboradores">
					<AgregarColaboradores eventId={eventId} isPublic={isPublic} />
				</Route>
				<Route exact path="/crearEvento/:eventId(\d+)/publicar">
					{isPublic
						? <PublicarEvento eventId={eventId} isPublic={isPublic} />
						: <Redirect to={urlPaso(eventId, 2)} />}
				</Route>
				{/* cualquier otra ruta (incluidas las viejas /segundoPaso, etc.) arranca de cero */}
				<Redirect to="/crearEvento/creacion" />
			</Switch>
		);
	};

	return (
		<>
			<Container className="my-3" maxWidth="lg">
				<div>
					<h3 style={{ color: "#001D67" }}>
						Crear evento &gt; <span style={{ fontWeight: 700 }}>{steps[activeStep]}</span>
					</h3>
				</div>
				<div style={{ textAlign: "center" }}>
					{renderStepper()}
				</div>
			</Container>

			{renderPasos()}

			{/* en el primer paso avanza el propio botón del formulario (crear o guardar) */}
			{eventId && activeStep > 0 && (
				<Container className="my-3" maxWidth="lg">
					<Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, marginRight: "20px", marginBottom: "20px" }}>
						<Button
							variant="outlined"
							onClick={handleBack}
							disabled={isFinalizingEvent}
							sx={{ color: '#5656ff', borderColor: '#5656ff' }}
						>
							Atrás
						</Button>
						<Box sx={{ flex: '1 1 auto' }} />

						<Button
							variant="contained"
							onClick={handleNext}
							disabled={!canProceed()}
							sx={{
								backgroundColor: '#5656ff',
								'&:hover': {
									backgroundColor: '#4545ee'
								}
							}}
						>
							{isFinalizingEvent && (
								<CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
							)}
							{getButtonText()}
						</Button>
					</Box>
				</Container>
			)}
		</>
	);
};
